import { type CallhomeCamera, type CallhomeStream } from '@solinkcorp/api-types';
import { VIDEO_ASPECT_HEIGHT, VIDEO_ASPECT_RATIO, VIDEO_ASPECT_WIDTH } from 'vd-constants';

/**
 * The width of camera thumbnails uploaded to S3 from checkin details.
 *
 * These are always normalized to 640 pixels wide:
 * https://github.com/SolinkCorp/connect-checkin/blob/5798a72c97d3b06096e4cc64f4507386d27952ab/config/environment/index.js#L27
 */
const S3_IMAGE_WIDTH = 640;

/**
 * Given the dimensions of an image `src`, and a target width or height, compute the width and height of the
 * resulting image.  If `src` is undefined, returns `{width: undefined, height: undefined}`.
 *
 * @example
 *  matchAspectRation({ width: 640, height: 320 }, { width: 320 }) // Returns { width: 320, height: 240 }
 */
export function matchAspectRatio(
  src: { width: number; height: number } | undefined,
  target: { width?: number; height?: number } | undefined,
) {
  let { width, height } = target || {};

  if (src) {
    if (!width && !height) {
      // Nothing to match against...
    } else if (width) {
      height = (width / src.width) * src.height;
    } else if (height) {
      width = (height / src.height) * src.width;
    }
  }

  return { width, height };
}

/**
 * Get the dimensions of a camera thumbnail.
 *
 * This function assumes that the thumbnail is either a link to an S3 URL, or
 * a
 */
export function getThumbnailDimensions(camera: CallhomeCamera): { width: number; height: number } {
  const thumbnail = camera?.thumbnail;

  if (!thumbnail) {
    return { width: VIDEO_ASPECT_WIDTH, height: VIDEO_ASPECT_HEIGHT };
  } else if (thumbnail.startsWith('data:image')) {
    // TODO: When is this a data URL?
    const memoryImg = document.createElement('img');
    memoryImg.src = thumbnail;
    return { width: memoryImg.width, height: memoryImg.height };
  }
  // Assume this
  return getS3ThumbnailDimensions(camera);
}

function getS3ThumbnailDimensions(camera: CallhomeCamera): { width: number; height: number } {
  // We already know the width.  In order to work out the height, we need to get
  // the aspect ratio of the stream.  connect-checkin will use the stream with
  // the highest BPS to generate the thumbnail.  See
  // https://github.com/SolinkCorp/connect-checkin/blob/5798a72c97d3b06096e4cc64f4507386d27952ab/services/checkin.js#L156.

  if (camera.streams.length === 0) {
    console.error(`Invalid camera ${camera.id} has no streams!`);
    return { width: S3_IMAGE_WIDTH, height: 320 };
  }

  let highestBpsStream: CallhomeStream = camera.streams[0];
  for (const stream of camera.streams) {
    if ((stream.average_bps ?? 0) > (highestBpsStream.average_bps ?? 0)) {
      highestBpsStream = stream;
    }
  }

  const height = Math.floor(
    S3_IMAGE_WIDTH *
      (highestBpsStream.dimensions
        ? highestBpsStream.dimensions.height / highestBpsStream.dimensions.width
        : 1 / VIDEO_ASPECT_RATIO),
  );

  return { width: S3_IMAGE_WIDTH, height };
}
